import { useTranslate } from "ra-core";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";

import { SegmentedControl } from "@/components/atomic-crm/tasks/SegmentedControl";
import { useIsMobile } from "@/hooks/use-mobile";

import "@/styles/market-intel.css";

import { CompetitorShow } from "./CompetitorShow";
import { CompetitorsView } from "./CompetitorsView";
import {
  COMPETITORS_BASE,
  COMPETITORS_TABS,
  competitorsTabOfPath,
  competitorsTabPath,
  isCompetitorDetailPath,
  type CompetitorsTab,
} from "./routes";
import { SwotView } from "./SwotView";
import { TrendsView } from "./TrendsView";

/**
 * "Competencia": what the competitors do (list + full profile per company),
 * the trends seen on social networks and the SWOT analyses of the market,
 * under /competencia/*. Same chrome as Mercado: a sticky glass segmented
 * control under the top bar on a phone, next to the title on a desktop.
 */
export const CompetitorsPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tab = competitorsTabOfPath(pathname);
  // A competitor's page has its own header; on a phone it hides the tabs.
  const isDetail = isCompetitorDetailPath(pathname);

  const options = COMPETITORS_TABS.map((entry) => ({
    value: entry.id,
    label: translate(`competition.tabs.${entry.id}`),
  }));
  const onChange = (next: CompetitorsTab) => navigate(competitorsTabPath(next));
  const label = translate("competition.tabs.label");

  return (
    <div
      className={
        isMobile ? "overflow-x-clip px-4" : "mx-auto w-full max-w-[1180px] pt-2"
      }
    >
      {isMobile && isDetail ? null : (
        <header
          className={
            isMobile
              ? "pb-2"
              : "mb-5 flex flex-wrap items-end justify-between gap-4"
          }
        >
          <h1
            className={
              isMobile
                ? "text-2xl leading-tight font-bold tracking-tight"
                : "text-2xl font-semibold tracking-tight"
            }
          >
            {translate("competition.title")}
          </h1>
          {isMobile ? null : (
            <SegmentedControl
              value={tab}
              options={options}
              onChange={onChange}
              label={label}
              fit
              className="w-[26rem]"
            />
          )}
        </header>
      )}
      {isMobile && !isDetail ? (
        <div className="mi-tabs-sticky">
          <SegmentedControl
            value={tab}
            options={options}
            onChange={onChange}
            label={label}
            glass
            size="sm"
          />
        </div>
      ) : null}
      <div className={isMobile ? "pt-1" : undefined}>
        <Routes>
          <Route index element={<CompetitorsView />} />
          <Route path="tendencias" element={<TrendsView />} />
          <Route path="foda" element={<SwotView />} />
          <Route path=":id" element={<CompetitorShow />} />
          <Route
            path="*"
            element={<Navigate to={COMPETITORS_BASE} replace />}
          />
        </Routes>
      </div>
    </div>
  );
};

CompetitorsPage.path = `${COMPETITORS_BASE}/*`;
